'use client';

import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';

import { NumberInputField } from '@/components/Inputs/NumberInputField';
import { SelectField } from '@/components/Inputs/SelectField';

export type AdvancedFilters = {
  lenders: string[];
  purposes: string[];
  productTypes: string[];
  loanAmount: [number | null, number | null];
  loanTerm: [number | null, number | null];
  timeInBiz: [number | null, number | null];
  monthlySales: [number | null, number | null];
  apr: [number | null, number | null];
  hideApr: boolean;
};

type Ranges = {
  loanAmount: [number | null, number | null];
  loanTerm: [number | null, number | null];
  timeInBiz: [number | null, number | null];
  monthlySales: [number | null, number | null];
  apr: [number | null, number | null];
};

type AdvancedFiltersModalProps = {
  open: boolean;
  onClose: () => void;
  filters: AdvancedFilters;
  setFilters: (_val: AdvancedFilters) => void;
  lenders: string[];
  purposes: string[];
  productTypes: string[];
  ranges: Ranges;
  includeLenderTiers: boolean;
};

export default function AdvancedFiltersModal({
  open,
  onClose,
  filters,
  setFilters,
  lenders,
  purposes,
  productTypes,
  includeLenderTiers,
  ranges: _ranges,
}: AdvancedFiltersModalProps) {
  const [draft, setDraft] = useState<AdvancedFilters>(filters);

  useEffect(() => {
    if (open) {
      setDraft({
        ...filters,
        loanAmount: [null, null],
        loanTerm: [null, null],
        timeInBiz: [null, null],
        monthlySales: [null, null],
        apr: [null, null],
      });
    }
  }, [open, filters]);

  const handleApply = () => {
    setFilters(draft);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      BackdropProps={{ sx: { backgroundColor: 'rgba(0,0,0,0.5)' } }}
      PaperProps={{
        sx: {
          backgroundColor: 'background.paper',
          borderRadius: 3,
          boxShadow: 6,
        },
      }}
    >
      <DialogTitle>Advanced Filters</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, mt: 1 }}>
          <SelectField
            label="Lenders"
            value={filters.lenders}
            onChange={(vals) => setFilters({ ...filters, lenders: vals as string[] })}
            options={lenders.map((l) => ({ label: l, value: l }))}
            multiple
          />

          <SelectField
            label="Purpose"
            value={draft.purposes[0] || ''}
            options={purposes.map((p) => ({ label: p, value: p }))}
            onChange={(val) => setDraft({ ...draft, purposes: Array.isArray(val) ? val : [val] })}
          />

          {includeLenderTiers && (
            <SelectField
              label="Product Type"
              value={filters.productTypes}
              onChange={(vals) => setFilters({ ...filters, productTypes: vals as string[] })}
              options={productTypes.map((l) => ({ label: l, value: l }))}
              multiple
            />
          )}

          <Box>
            <Typography variant="body1" fontWeight={600} mb={1}>
              Loan Amount ($)
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <NumberInputField
                  label="Min"
                  value={draft.loanAmount[0] || ''}
                  onChange={(val) =>
                    setDraft({
                      ...draft,
                      loanAmount: [val, draft.loanAmount[1]],
                    })
                  }
                  currency
                />
              </Grid>
              <Grid item xs={6}>
                <NumberInputField
                  label="Max"
                  value={draft.loanAmount[1] || ''}
                  onChange={(val) =>
                    setDraft({
                      ...draft,
                      loanAmount: [draft.loanAmount[0], val],
                    })
                  }
                  currency
                />
              </Grid>
            </Grid>
          </Box>

          <Box>
            <Typography variant="body1" fontWeight={600} mb={1}>
              Loan Term (months)
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <NumberInputField
                  label="Min"
                  value={draft.loanTerm[0] || ''}
                  onChange={(val) => setDraft({ ...draft, loanTerm: [val, draft.loanTerm[1]] })}
                />
              </Grid>
              <Grid item xs={6}>
                <NumberInputField
                  label="Max"
                  value={draft.loanTerm[1] || ''}
                  onChange={(val) => setDraft({ ...draft, loanTerm: [draft.loanTerm[0], val] })}
                />
              </Grid>
            </Grid>
          </Box>

          <Box>
            <Typography variant="body1" fontWeight={600} mb={1}>
              Time in Business (months)
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <NumberInputField
                  label="Min"
                  value={draft.timeInBiz[0] || ''}
                  onChange={(val) => setDraft({ ...draft, timeInBiz: [val, draft.timeInBiz[1]] })}
                />
              </Grid>
              <Grid item xs={6}>
                <NumberInputField
                  label="Max"
                  value={draft.timeInBiz[1] || ''}
                  onChange={(val) => setDraft({ ...draft, timeInBiz: [draft.timeInBiz[0], val] })}
                />
              </Grid>
            </Grid>
          </Box>

          <Box>
            <Typography variant="body1" fontWeight={600} mb={1}>
              Monthly Sales ($)
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <NumberInputField
                  label="Min"
                  value={draft.monthlySales[0] || ''}
                  onChange={(val) =>
                    setDraft({
                      ...draft,
                      monthlySales: [val, draft.monthlySales[1]],
                    })
                  }
                  currency
                />
              </Grid>
              <Grid item xs={6}>
                <NumberInputField
                  label="Max"
                  value={draft.monthlySales[1] || ''}
                  onChange={(val) =>
                    setDraft({
                      ...draft,
                      monthlySales: [draft.monthlySales[0], val],
                    })
                  }
                  currency
                />
              </Grid>
            </Grid>
          </Box>

          <Box>
            <Typography variant="body1" fontWeight={600} mb={1}>
              APR (%)
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <NumberInputField
                  label="Min"
                  value={draft.apr[0] || ''}
                  onChange={(val) => setDraft({ ...draft, apr: [val, draft.apr[1]] })}
                />
              </Grid>
              <Grid item xs={6}>
                <NumberInputField
                  label="Max"
                  value={draft.apr[1] || ''}
                  onChange={(val) => setDraft({ ...draft, apr: [draft.apr[0], val] })}
                />
              </Grid>
            </Grid>
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={draft.hideApr}
                onChange={(e) => setDraft({ ...draft, hideApr: e.target.checked })}
              />
            }
            label="Hide lenders with APR hidden"
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button onClick={handleApply} variant="contained">
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
}
